import { useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import InputAdornment from "@mui/material/InputAdornment";
import {
  useUpdate,
  useNotify,
  SimpleForm,
  Edit,
  useResourceContext,
  required,
  maxLength,
  useRefresh,
  minLength,
  TextInput,
  useTranslate,
  ReferenceInput,
  AutocompleteInput,
} from "react-admin";
import MuiDatePickerInput from "../../common/MuiDatePickerInput";
import SaveDeleteBtns from "../../saveDeleteBtns/SaveDeleteBtn";
import SaleOrderItemForm from "./components/SaleOrderItemForm";
import DependOnAutocompleteInput from "../../common/DependOnAutocompleteInput";
import TotalInput from "../common/TotalInput";
import currentDate from "../../../utils/currentDate";
import parserFormatNumInput from "../../../utils/parserFormatNumInput";
import {
  OptionRenderer,
  inputText,
  matchSuggestion,
  filterToQuery,
} from "../common/CustomSelectOption";
import CustomerSelect from "../common/CustomerSelect";
import CodeNumberInput from "../../common/CodeNumberInput";
import CategorySelectForSO from "../common/CategorySelectForSO";

const validateNumber = [required(), minLength(13), maxLength(13)];
const validateDate = [required(), minLength(10), maxLength(10)];
const validateCustomer = [required(), minLength(36), maxLength(36)];

export default function SaleOrderEditDialog({ open, setOpen, recordId }) {
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const resource = useResourceContext(); // cuman nama resource
  const translate = useTranslate();

  const save = useCallback(
    async (values) => {
      try {
        await update(
          resource,
          { id: values.id, data: values },
          {
            returnPromise: true,
            onSuccess: (data) => {
              notify("ra.notification.updated", {
                messageArgs: { smart_count: 1 },
              });
              setOpen(false);
              refresh();
            },
          }
        );
      } catch (error) {
        if (error.body && error.body.message) {
          notify(error.body.message, { type: "error" });
        } else if (error.body && error.body.errors) {
          if (error.body.errors === "403") {
            // munculkan notifikasi erro ketika sudah approved
            notify("mine.notification.403_error", { type: "error" });
          } else {
            // The shape of the returned validation errors must match the shape of the form
            return error.body.errors;
          }
        }
      }
    },
    [update, notify, refresh, resource, setOpen]
  );

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted={false}
      fullWidth={true}
      maxWidth={false}
    >
      <Edit
        title={" "}
        id={recordId}
        actions={false}
        sx={{ "& .RaEdit-main": { mt: 0 } }}
      >
        <SimpleForm
          warnWhenUnsavedChanges
          onSubmit={save}
          toolbar={<SaveDeleteBtns callback={handleClose} />}
          mode="onBlur"
          reValidateMode="onBlur"
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 0,
            }}
          >
            <DialogTitle>
              {translate("mine.dialog.edit_sale_order")}
            </DialogTitle>
            <IconButton onClick={handleClose} sx={{ mr: "11px" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ alignSelf: "normal" }}>
            <TextInput source="id" sx={{ display: "none" }} />

            <CodeNumberInput
              // open={open}
              dependOn="date"
              variant="outlined"
              source="code"
              label="mine.label.receipt_no"
              fullWidth
              validate={validateNumber}
              disabled
            />
            <MuiDatePickerInput
              variant="outlined"
              source="date"
              fullWidth
              isRequired
              validate={validateDate}
              defaultValue={currentDate()}
            />

            <CustomerSelect
              // FIXME: et.... tapi data customer yg didapat ini bisa jadi harusnya
              // punya kriteria tertentu misal customer yg tidak punya hutang, atau
              // lainnya
              validate={validateCustomer}
              source="customer_id"
              reference="customers"
              label="mine.label.customer"
              orderField="code"
              optionText={<OptionRenderer />}
              inputText={inputText}
              matchSuggestion={matchSuggestion}
            />

            <DependOnAutocompleteInput
              variant="outlined"
              source="customer_address_id"
              label="mine.label.address"
              isRequired
              fullWidth
              validate={validateCustomer}
              resource="customer-addresses"
              queryName="customer_id"
              dependOn="customer_id"
            />

            <ReferenceInput
              source="salesman_id"
              reference="salesmen"
              perPage={10}
              sort={{ field: "salesmen.code", order: "DESC" }}
              filter={{ "salesmen.active": "1" }}
              enableGetChoices={() => true}
              // enableGetChoices={(filters) => !!filters.all}
            >
              <AutocompleteInput
                variant="outlined"
                fullWidth
                validate={validateCustomer}
                optionValue="id"
                optionText={<OptionRenderer />}
                inputText={inputText}
                matchSuggestion={matchSuggestion}
                filterToQuery={filterToQuery}
                openOnFocus
              />
            </ReferenceInput>

            <ReferenceInput
              source="category_id"
              reference="categories"
              perPage={20}
              sort={{ field: "display_order", order: "ASC" }}
              enableGetChoices={() => true}
            >
              <CategorySelectForSO
                source="category_id"
                label="mine.label.category"
                variant="outlined"
                fullWidth
                validate={validateCustomer}
                optionValue="id"
                optionText="name"
              />
            </ReferenceInput>

            <Box
              sx={{
                  maxHeight: '300px', // Sesuaikan tinggi maksimum sesuai kebutuhan Anda
                  overflowY: 'auto',
                  border: '1px solid #ccc', // Opsional: untuk visualisasi batas
                  borderRadius: '4px',
                  marginBottom: '24px',
                  p: 1, // Opsional: padding
              }}
              >
              <SaleOrderItemForm />
            </Box>

            <TotalInput
              variant="outlined"
              source="total"
              fullWidth
              disabled
              defaultValue={0}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">Rp</InputAdornment>
                ),
                // endAdornment: <InputAdornment position="end">/Box</InputAdornment>,
              }}
              {...parserFormatNumInput}
            />
          </DialogContent>
        </SimpleForm>
      </Edit>
    </Dialog>
  );
}
